import {
  CSSProperties,
  forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState,
} from 'react';
import { Autocomplete } from '@mui/material';
import { DropDownMenuItem } from '../DropdownMenu';
import styles from './sharedAutoComplete.module.css';

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/jsx-props-no-spreading */
export interface SharedAutoCompleteProps<T=any> {
  value?: any
  options: DropDownMenuItem<T>[]
  onSelect: (value: any) => void
  onDeselect?: () => void
  onTextInput?: (value: string) => void
  onBlur?: () => void
  onBlurSetTextAsSelected?: boolean
  popperZIndex?: number
  disablePortal?: boolean
  searchIcon?: boolean
  placeholder?: string
  style?: CSSProperties
  menuStyle?: CSSProperties
}

export interface SharedAutoCompleteRef {
  getValue: () => any
}

export default forwardRef<SharedAutoCompleteRef, SharedAutoCompleteProps>((props, ref) => {
  const { onBlurSetTextAsSelected = true } = props;
  const [text, setText] = useState<any>(props.value);
  const [inputValue, setInputValue] = useState<string>('');
  const [lastSelectedText, setLastSelectedText] = useState<string>(props.value || '');
  const textRef = useRef<any>(props.value);
  const refInput = useRef<HTMLInputElement>(null);

  const handleTextInput = (value: string) => {
    setText(value);
    if (props.onTextInput) {
      props.onTextInput(value);
    }
  };

  const handleSelect = useCallback((selected: any) => {
    if (!selected.value && props.onDeselect) {
      props.onDeselect();
      return;
    }
    props.onSelect(selected);
  }, [props]);

  const handleBlur = useCallback(() => {
    if (props.onBlur) {
      props.onBlur();
    }
    if (onBlurSetTextAsSelected && inputValue !== lastSelectedText) {
      setText(lastSelectedText);
      setInputValue(lastSelectedText);
      textRef.current = lastSelectedText;
    }
  }, [props, inputValue, lastSelectedText]);

  useImperativeHandle(ref, () => ({
    getValue() {
      return textRef.current;
    },
  }));

  useEffect(() => {
    refInput.current?.focus();
    refInput.current?.click();
  }, []);

  useEffect(() => {
    setText(props.value || '');
    setLastSelectedText(props.value || '');
    textRef.current = props.value || '';
  }, [props.value]);

  return (
    <div style={props.style}>
      <Autocomplete
        freeSolo
        disablePortal={props.disablePortal}
        blurOnSelect
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.text || '')}
        isOptionEqualToValue={(option, value) => option === value}
        value={text}
        onChange={(_event: any, newValue: any) => {
          if (newValue != null) {
            setText(newValue.text);
            setLastSelectedText(newValue.text);
            textRef.current = newValue.text;
            handleSelect(newValue);
          }
        }}
        onBlurCapture={handleBlur}
        inputValue={inputValue}
        onInputChange={(_event, newInputValue) => {
          setInputValue(newInputValue);
          handleTextInput(newInputValue);
        }}
        id="controllable-states-demo"
        options={props.options}
        sx={{
          width: '100%',
          '& + .MuiAutocomplete-popper': {
            '& .MuiAutocomplete-listbox': {
              margin: 0,
              padding: 0,
            },
            '& .MuiAutocomplete-option': {
              display: 'block',
              minHeight: '40px',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              margin: 0,
              '&:hover': {
                backgroundColor: '#D2D2D2',
              },
              '&:not(:hover)': {
                backgroundColor: 'transparent',
              },
            },
          },
        }}
        renderInput={(params) => (
          <div ref={params.InputProps.ref} className={styles.inputContainer}>
            {props.searchIcon && <span className={styles.searchIcon}>&#xF002;</span>}
            <input
              type="text"
              {...params.inputProps}
              placeholder={props.placeholder}
              style={{ width: '100%' }}
              className={styles.AutoCompleteInputStyle}
            />
          </div>
        )}
        componentsProps={props.popperZIndex ? {
          popper: {
            sx: { zIndex: props.popperZIndex },
          },
        } : undefined}
        ListboxProps={{
          style: props.menuStyle,
        }}
      />
    </div>
  );
});
